import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import isEqual from 'lodash/isEqual'
import {
  fetchAisles,
  fetchDepartment,
  updateAisle,
  type FetchDepartmentAisleProductsQuery,
} from 'legacy/common/api'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { type ScopedRouteParams } from 'legacy/common/types'
import withRetailerRouter from 'legacy/common/withRouter'
import DepartmentView from 'legacy/routes/catalog/departments/DepartmentView'
import { type Department, type Aisle } from 'legacy/routes/catalog/types'
import { type Product } from 'legacy/routes/catalog/types/product.types'
import {
  withNotificationsContext,
  type NotificationsContextValue,
} from 'legacy/contexts/notifications/NotificationsContext'
import { compactObject } from 'utils/objects/compact'
import { type FetchProductsInjectedProps } from 'legacy/routes/catalog/departments/utils/types'
import { withFetchAisleProducts } from 'legacy/routes/catalog/departments/utils/withFetchAisleProducts'

interface DepartmentRouteParams extends ScopedRouteParams {
  department_id: string
  aisle_id: string
}

interface Query extends FetchDepartmentAisleProductsQuery {}

interface Props
  extends RetailerRouteComponentProps<DepartmentRouteParams, Query>,
    NotificationsContextValue,
    FetchProductsInjectedProps {
  onMount?(c: DepartmentRoute): void
}

interface State {
  department?: Department
  aisles?: Aisle[]
  productsPromise?: Promise<{ data?: Product[] }>
  params?: DepartmentRouteParams
  query: Partial<Query>
  onlyPrimaryProducts: boolean
}

const buildCurrentQuery = (
  {
    query: { page = '1', inventory_area_ids, region_ids },
    match: {
      params: { aisle_id, department_id },
    },
  }: Props,
  onlyPrimaryProducts: boolean
) =>
  compactObject({
    page: parseInt(page, 10),
    aisle_ids: aisle_id ? [aisle_id] : undefined,
    department_ids: [department_id],
    inventory_area_ids,
    region_ids,
    only_primary_products: onlyPrimaryProducts,
  })

class DepartmentRoute extends Component<Props, State> {
  static getDerivedStateFromProps(props: Props, prevState: State) {
    const {
      query,
      match: { params },
    } = props
    const { params: paramsPrev, query: queryPrev } = prevState
    const defaultOnlyPrimaryProducts = false

    if (!isEqual(query, queryPrev) || !isEqual(params, paramsPrev)) {
      return {
        params,
        query,
        productsPromise: props.fetchProducts(
          params,
          buildCurrentQuery(props, defaultOnlyPrimaryProducts)
        ),
        onlyPrimaryProducts: defaultOnlyPrimaryProducts,
      }
    }
    return null
  }

  state: State = {
    department: undefined,
    aisles: undefined,
    params: undefined,
    query: {},
    onlyPrimaryProducts: false,
  }

  async componentDidMount() {
    const {
      match: { params },
      onMount,
    } = this.props

    const [{ data: department }, { data: aisles }] = await Promise.all([
      fetchDepartment(params) as Promise<{ data: Department }>,
      fetchAisles(params) as Promise<{ data: Aisle[] }>,
    ])

    const productsPromise = this.props.fetchProducts(params, buildCurrentQuery(this.props, false))

    this.setState({
      department,
      aisles,
      productsPromise,
    })

    if (onMount) {
      onMount(this)
    }
  }

  getAisles = async () => {
    const {
      match: { params },
    } = this.props

    const { data } = await (fetchAisles(params) as Promise<{ data: Aisle[] }>)

    this.setState({
      aisles: data,
    })
  }

  onAisleDelete = (aisleId: number) => {
    const { department_id: departmentId } = this.props.match.params
    const { url, history } = this.props
    const { aisles } = this.state
    if (aisles) {
      this.setState({ aisles: aisles.filter(aisle => String(aisle.id) !== String(aisleId)) })
      history.push(url('aisles', { department_id: String(departmentId) }))
    }
  }

  onAislesOrderChange = async (aisleId: number, rankOffsetPosition: number) => {
    const {
      match: { params },
    } = this.props
    const { aisles } = this.state

    if (!aisles) {
      return
    }

    const newAisles = aisles.filter(aisle => aisle.id !== aisleId)
    newAisles.splice(rankOffsetPosition, 0, aisles.find(aisle => aisle.id === aisleId) as Aisle)
    this.setState({ aisles: newAisles })

    try {
      await updateAisle(params, aisleId, { rank_offset_position: rankOffsetPosition })

      this.props.notify(<FormattedMessage id="departments.aisles.sort_aisle_success" />)
    } catch (_e) {
      this.props.notifyError(<FormattedMessage id="departments.aisles.sort_aisle_failure" />)

      this.setState({ aisles })
    }
  }

  onToggleOnlyPrimaryProducts = () => {
    const { onlyPrimaryProducts } = this.state
    const {
      match: { params },
    } = this.props

    this.setState({
      onlyPrimaryProducts: !onlyPrimaryProducts,
      productsPromise: this.props.fetchProducts(
        params,
        buildCurrentQuery(this.props, !onlyPrimaryProducts)
      ),
    })
  }

  render() {
    const { department, aisles, productsPromise, onlyPrimaryProducts } = this.state

    return (
      <DepartmentView
        department={department}
        aisles={aisles}
        productsPromise={productsPromise}
        onAisleDelete={this.onAisleDelete}
        onAislesOrderChange={this.onAislesOrderChange}
        onlyPrimaryProducts={onlyPrimaryProducts}
        onToggleOnlyPrimaryProducts={this.onToggleOnlyPrimaryProducts}
        getAisles={this.getAisles}
        useMesh={this.props.useMesh}
      />
    )
  }
}

export default withFetchAisleProducts(withRetailerRouter(withNotificationsContext(DepartmentRoute)))
